import React, { useEffect, useMemo } from "react";
import {
  CalendarClock,
  CheckCircle2,
  Clock,
  FileText,
  Image as ImageIcon,
  Paperclip,
  Phone,
  User,
} from "lucide-react";

const formatScheduleLabel = (scheduleTime) => {
  if (!scheduleTime) return "Not scheduled";
  const date = new Date(scheduleTime);
  if (Number.isNaN(date.getTime())) return "Not scheduled";
  return date.toLocaleString();
};

const isImageUrl = (url = "") => /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(url);

const extractFileNameFromUrl = (url = "") => {
  try {
    const parsed = new URL(url);
    const segments = parsed.pathname.split("/").filter(Boolean);
    if (segments.length > 0) {
      return decodeURIComponent(segments[segments.length - 1]);
    }
    return "Attachment";
  } catch {
    const fallbackSegments = url.split("/").filter(Boolean);
    if (fallbackSegments.length > 0) {
      return fallbackSegments[fallbackSegments.length - 1];
    }
    return "Attachment";
  }
};

const formatFileSize = (size = 0) => {
  if (!Number.isFinite(size) || size <= 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let value = size;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  const precision = value >= 10 || unitIndex === 0 ? 0 : 1;
  return `${value.toFixed(precision)} ${units[unitIndex]}`;
};

const buildAttachmentEntry = (item, index) => {
  const isFile = typeof File !== "undefined" && item instanceof File;

  if (isFile) {
    const isImage = item.type.startsWith("image/");
    const objectUrl = URL.createObjectURL(item);

    return {
      id: `file-${index}`,
      type: isImage ? "image" : "file",
      name: item.name || `Attachment ${index + 1}`,
      sizeLabel: formatFileSize(item.size),
      url: isImage ? objectUrl : "",
      href: objectUrl,
      revoke: () => URL.revokeObjectURL(objectUrl),
    };
  }

  if (typeof item === "string" && item.trim()) {
    const trimmed = item.trim();
    const isImage = isImageUrl(trimmed);

    return {
      id: `link-${index}`,
      type: isImage ? "image" : "link",
      name: isImage ? `Image ${index + 1}` : extractFileNameFromUrl(trimmed),
      sizeLabel: "",
      url: isImage ? trimmed : "",
      href: trimmed,
    };
  }

  return null;
};

const renderAttachmentCard = (attachment) => {
  if (attachment.type === "image") {
    return (
      <a
        key={attachment.id}
        href={attachment.href}
        target="_blank"
        rel="noopener noreferrer"
        className="group overflow-hidden rounded-2xl border border-indigo-200/80 bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
      >
        <div className="relative h-32 w-full overflow-hidden bg-indigo-100/60">
          {attachment.url ? (
            <img
              src={attachment.url}
              alt={attachment.name}
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-indigo-400">
              <ImageIcon className="h-7 w-7" />
            </div>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 px-3 py-2">
          <span className="truncate text-xs font-medium text-slate-700">{attachment.name}</span>
          <span className="text-[11px] text-slate-500">View</span>
        </div>
      </a>
    );
  }

  const Icon = attachment.type === "link" ? Paperclip : FileText;
  const descriptor = attachment.sizeLabel || (attachment.type === "link" ? "External link" : "File");

  return (
    <a
      key={attachment.id}
      href={attachment.href}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex items-center gap-3 rounded-2xl border border-indigo-200/80 bg-white px-3 py-3 shadow-sm transition-all hover:-translate-y-0.5 hover:bg-indigo-50/60 hover:shadow-md"
    >
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-indigo-100 text-indigo-600">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium text-slate-700">{attachment.name}</p>
        <p className="text-[11px] text-slate-500">{descriptor}</p>
      </div>
      <span className="text-[11px] font-medium text-indigo-600 group-hover:text-violet-600">Open</span>
    </a>
  );
};

const WhatsAppLivePreview = ({
  title = "Live Preview",
  receiverMobileNo,
  receiverName,
  message,
  scheduleTime,
  attachments = [],
}) => {
  const preparedMessage = message?.trim() || "Write a message to generate a live preview.";
  const hasMessage = Boolean(message?.trim());
  const receiverLabel = receiverName || "Recipient not selected";
  const receiverNumberLabel = receiverMobileNo || "No number added";
  const scheduleLabel = formatScheduleLabel(scheduleTime);
  const messageLength = message?.trim()?.length || 0;

  const preparedAttachments = useMemo(() => {
    if (!Array.isArray(attachments)) return [];
    return attachments
      .filter(Boolean)
      .map((item, index) => buildAttachmentEntry(item, index))
      .filter(Boolean);
  }, [attachments]);

  useEffect(() => {
    return () => {
      preparedAttachments.forEach((attachment) => {
        if (typeof attachment.revoke === "function") {
          attachment.revoke();
        }
      });
    };
  }, [preparedAttachments]);

  return (
    <div className="relative overflow-hidden rounded-3xl border border-indigo-200/80 bg-white shadow-[0_24px_65px_-35px_rgba(76,29,149,0.4)]">
      <div className="relative overflow-hidden border-b border-indigo-300/30 bg-gradient-to-r from-indigo-600 via-violet-600 to-indigo-700 px-4 py-3 text-white sm:px-5">
        <div
          className="pointer-events-none absolute inset-0 opacity-35"
          style={{
            backgroundImage:
              "radial-gradient(circle at 12% 28%, rgba(255,255,255,0.32) 0%, transparent 38%), radial-gradient(circle at 82% 14%, rgba(255,255,255,0.25) 0%, transparent 36%)",
          }}
        />
        <div className="relative flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-indigo-100/90">{title}</p>
            <h4 className="mt-1 text-base font-semibold text-white sm:text-lg">Preview Workspace</h4>
            <p className="mt-1 text-xs text-indigo-100/90">Review your content before sending</p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-white/30 bg-white/15 px-2.5 py-1 text-[11px] font-medium text-white backdrop-blur">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Live
          </span>
        </div>
      </div>

      <div className="relative min-h-[360px] max-h-[540px] overflow-y-auto bg-gradient-to-br from-indigo-50/80 via-white to-violet-50/50 p-4 sm:p-5">
        <div
          className="pointer-events-none absolute inset-0 opacity-35"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgba(79,70,229,0.12) 1px, transparent 0)",
            backgroundSize: "16px 16px",
          }}
        />

        <div className="relative z-10 space-y-4">
          {/* <div className="grid gap-2 sm:grid-cols-3">
            <div className="rounded-xl border border-indigo-200/80 bg-white/85 p-3 backdrop-blur">
              <p className="mb-1 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                <User className="h-3.5 w-3.5" />
                Recipient
              </p>
              <p className="truncate text-xs font-semibold text-slate-700">{receiverLabel}</p>
            </div>

            <div className="rounded-xl border border-indigo-200/80 bg-white/85 p-3 backdrop-blur">
              <p className="mb-1 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                <Phone className="h-3.5 w-3.5" />
                Mobile
              </p>
              <p className="truncate text-xs font-semibold text-slate-700">{receiverNumberLabel}</p>
            </div>

            <div className="rounded-xl border border-indigo-200/80 bg-white/85 p-3 backdrop-blur">
              <p className="mb-1 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                <CalendarClock className="h-3.5 w-3.5" />
                Schedule
              </p>
              <p className="truncate text-xs font-semibold text-slate-700">{scheduleLabel}</p>
            </div>
          </div> */}

          <section className="rounded-2xl border border-indigo-200/80 bg-white/90 p-4 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <h5 className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">Message Content</h5>
              <span className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2.5 py-1 text-[11px] font-medium text-indigo-700">
                <Clock className="h-3 w-3" />
                {messageLength} chars
              </span>
            </div>
            <p
              className={`whitespace-pre-wrap break-words text-sm leading-relaxed ${
                hasMessage ? "text-slate-700" : "italic text-slate-500"
              }`}
            >
              {preparedMessage}
            </p>
          </section>

          <section className="rounded-2xl border border-indigo-200/80 bg-white/90 p-4 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <h5 className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">
                <Paperclip className="h-3.5 w-3.5" />
                Attachments
              </h5>
              <span className="rounded-full bg-violet-100 px-2.5 py-1 text-[11px] font-medium text-violet-700">
                {preparedAttachments.length}
              </span>
            </div>

            {preparedAttachments.length > 0 ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {preparedAttachments.map((attachment) => renderAttachmentCard(attachment))}
              </div>
            ) : (
              <div className="flex items-center gap-2 rounded-xl border border-dashed border-indigo-200 bg-indigo-50/50 px-3 py-4 text-xs text-slate-500">
                <FileText className="h-4 w-4 text-indigo-400" />
                No attachments added yet.
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppLivePreview;
